import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useTranslation } from "react-i18next";

import type { PaymentMethod } from "~/providers/payment-method-provider";
import { usePaymentMethodManager } from "~/managers/use-payment-method-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { usePaymentMethodStore } from "~/stores/use-payment-method-store";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import PaymentMethodForm from "./payment-method-form";

export default function PaymentMethodDialog() {
  const {
    paymentMethodFormOpen,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    setPaymentMethodFormOpen,
  } = usePaymentMethodStore();

  const { checkUnsavedChanges } = useUnsavedChanges();

  const { deletePaymentMethod } = usePaymentMethodManager();

  const { t } = useTranslation("common");

  const handleDialog = (open: boolean, force?: boolean) => {
    if (open === false) {
      if (force !== true) {
        const result = checkUnsavedChanges();
        if (result === true) return;
      }
      setSelectedPaymentMethod(undefined);
    }
    setPaymentMethodFormOpen(open);
  };

  const handleDelete = async (paymentMethod: PaymentMethod) => {
    const result = await deletePaymentMethod(paymentMethod);
    if (result === true) {
      handleDialog(false, true);
    }
  };

  return (
    <Dialog open={paymentMethodFormOpen} onOpenChange={handleDialog}>
      <DialogContent className="max-w-xl w-full">
        <DialogHeader>
          <DialogTitle>{t("PaymentMethod.payment_method")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
          <PaymentMethodForm
            paymentMethod={selectedPaymentMethod}
            onClose={(force) => handleDialog(false, force)}
          />
        </div>
        <DialogFooter>
          <div className="flex flex-row gap-2 w-full justify-between">
            {selectedPaymentMethod?.isDefault === false && (
              <Button
                onClick={() => {
                  void handleDelete(selectedPaymentMethod);
                }}
                className="w-full"
                variant="destructive"
              >
                {t("delete")}
              </Button>
            )}
            <Button
              // disabled={isLoading}
              type="submit"
              variant="success"
              form="payment-method-form"
              className="w-full"
            >
              {selectedPaymentMethod ? t("update") : t("create")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
